import type { WizParsedData } from '@/sections/poe/processors/wiz/types';
import type { POEContent } from '@/sections/poe/types/poe.types';

export const parseWizData = (poeData: POEContent): WizParsedData => {
  const contentStr =
    typeof poeData === 'string' ? poeData : JSON.stringify(poeData);
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  let wizData: any = null;
  let vulnerabilityDescription = '';
  let wizId = null;
  const processedKeys: string[] = []; // Track what keys we've processed

  try {
    wizData = JSON.parse(contentStr);
  } catch {
    const lines = contentStr.split('\n');
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const simpleData: any = {};
    let hasData = false;

    for (const line of lines) {
      const trimmed = line.trim();
      if (!trimmed || trimmed.startsWith('#')) continue;

      const colonIndex = trimmed.indexOf(':');
      if (colonIndex > 0) {
        const key = trimmed.substring(0, colonIndex).trim();
        const value = trimmed.substring(colonIndex + 1).trim();
        if (key && value) {
          simpleData[key] = value;
          hasData = true;
        }
      }
    }

    if (hasData) {
      wizData = simpleData;
    } else {
      const descMatch = contentStr.match(
        /#{1,4}\s+(?:Vulnerability\s+)?Description\s*([\s\S]*?)(?:--or--|$)/
      );
      if (descMatch) {
        vulnerabilityDescription = descMatch[1].trim();
      }

      const resourceMatch = contentStr.match(/--or--\s*({.*})/s);
      if (resourceMatch) {
        try {
          wizData = JSON.parse(resourceMatch[1]);
        } catch (parseError) {
          console.warn('Failed to parse embedded resource data:', parseError);
        }
      }
    }
  }

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  let resourceData: any = null;
  if (wizData) {
    wizId = wizData.wiz_id;
    if (!vulnerabilityDescription && wizData.description) {
      vulnerabilityDescription = wizData.description;
    }
    resourceData = wizData;
  }

  let isSimpleFormat = false;
  let isSubscriptionFormat = false;
  let isMultiSectionFormat = false;

  if (wizData) {
    const multiSectionKeys = [
      'bucket',
      'data finding',
      'endpoint',
      'iam binding',
      'service account',
    ];
    const hasMultiSections = multiSectionKeys.some(key => wizData[key]);

    const hasDataFindingExamples =
      (Array.isArray(wizData.dataFindingExamples) &&
        wizData.dataFindingExamples.length > 0) ||
      (Array.isArray(wizData['Data Finding Examples']) &&
        wizData['Data Finding Examples'].length > 0);

    const hasPhoneDataArray = Object.values(wizData).some(
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      (value: any) =>
        Array.isArray(value) &&
        value.length > 0 &&
        value[0] &&
        typeof value[0] === 'object' &&
        (value[0].value || value[0].valueForAnalytics || value[0].path)
    );

    const hasDataCategory =
      typeof wizData === 'object' &&
      (wizData.DataCategory === 'DataCategoryPII' ||
        wizData['Data Category'] === 'DataCategoryPII' ||
        hasDataFindingExamples ||
        hasPhoneDataArray);

    const hasDataFindingExamplesKey =
      wizData &&
      Array.isArray(wizData['Data Finding Examples']) &&
      wizData['Data Finding Examples'].length > 0;

    if (
      hasMultiSections ||
      hasDataFindingExamples ||
      hasDataCategory ||
      hasDataFindingExamplesKey
    ) {
      isMultiSectionFormat = true;
    } else if (
      !wizData['configuration finding'] &&
      !wizData['endpoint'] &&
      !wizData['secret data']
    ) {
      if (wizData.subscription && wizId) {
        isSubscriptionFormat = true;
      } else {
        const hasVulnFields =
          wizData.cvss_base ||
          wizData.version ||
          wizData.fixed_version ||
          wizData.os;
        if (hasVulnFields && wizId) {
          isSimpleFormat = true;
        }
      }
    }
  }

  if (typeof poeData === 'object' && poeData !== null) {
    if ('wiz_id' in poeData) {
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      wizId = (poeData as any).wiz_id;
    }

    Object.keys(poeData).forEach(key => {
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      const value = (poeData as any)[key];
      if (
        key.toLowerCase().includes('description') &&
        typeof value === 'string'
      ) {
        if (!vulnerabilityDescription) {
          vulnerabilityDescription = value;
        }
      }
      if (key === 'subscription' && !resourceData) {
        resourceData = { subscription: value };
      }
    });
  }

  // Pre-extract Data Finding Examples array and delete from wizData
  let dataFindingExamplesArray: unknown[] | null = null;
  if (
    wizData &&
    wizData['Data Finding Examples'] &&
    Array.isArray(wizData['Data Finding Examples'])
  ) {
    dataFindingExamplesArray = wizData['Data Finding Examples'];
    if (dataFindingExamplesArray.length > 0) {
      delete wizData['Data Finding Examples'];
    }
  }

  return {
    wizData,
    wizId,
    vulnerabilityDescription,
    resourceData,
    processedKeys,
    isSimpleFormat,
    isSubscriptionFormat,
    isMultiSectionFormat,
    dataFindingExamplesArray,
  };
};
